import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useOperacion } from '../../hooks/useOperacion';
import { operacion } from '../../models/operacion';
import { EnumDestinoCredito, enumTipoPersona, Prepago, StatusCode, TipoFuncionalidad } from '../../../../utils/enums';
import RefinanciamientoForm from './refinanciamientoForm';
import AdquisicionForm from './adquisicionForm';
import TablasDesarrolloForm from './tablasDesarrolloForm';
import SubsidioForm from './subsidioForm';
import { SubsidioMutuo } from '../../hooks/useSubsidioMutuo';
import { registerLocale } from 'react-datepicker'
import { es, is } from 'date-fns/locale'
import InputSelect from '../../../../components/inputs/yup/materialui/inputSelect';
import InputNumber from '../../../../components/inputs/yup/materialui/inputNumber';
import InputSwitch from '../../../../components/inputs/yup/materialui/inputSwitch';
import { Card, CardContent, Divider, Grid2, Typography } from '@mui/material';
import InputFecha from '../../../../components/inputs/yup/materialui/inputFecha';
import OtroCreditoForm from './otroCreditoForm';
import InputText from '../../../../components/inputs/yup/materialui/inputText';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import { Close, Edit, SaveAlt } from '@mui/icons-material';
import { copyOperacion, putOperacion } from '../../services/servicesOperacion';
import { useToast } from '../../../../hooks/useToast';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import { PermissionGuard } from '../../../../components/auth/ProtectedRoute';
import { useUserRolesContext } from '../../../../context/userRolesContext';
import { DetalleMutuoPermissionGuard, useDetalleMutuoAccess } from '../../../../components/auth/DetalleMutuoPermissionGuard';

registerLocale('es', es)

interface FormProps<T> {
    // onSubmit: (data: T) => void;
    idMutuo: number;
    isComplementario: boolean;
    esMutuoNormalyConComplementario: boolean;
    idSolicitud: string | undefined;
    isDesactivado: boolean;
    esVigente?: boolean;
}

const tipoProductosSinCoutaMaxima: string[] = ['MHT', 'MHD'];

const validationSchema = yup.object<operacion>().shape({
    tipoMutuo: yup.string().nullable(),
    tipoDeudor: yup.string().required('Campo requerido'),
    tipoProducto: yup.string().required('Campo requerido'),
    destinoCredito: yup.string().required('Campo requerido'),
    ejecutivo: yup.string().nullable(),

    prepago: yup.string().required('Campo requerido'), // prepago solo actualiza si es administrador
    comisionPrepago: yup.number().nullable().when(['prepago'], {
        is: (prepago: string) => prepago === Prepago.ConCosto,
        then: s => s.required('Campo requerido'),
        otherwise: s => s.notRequired()
    }),

    diaPago: yup.number().nullable(),

    mesesGraciasK: yup.number().required("Campo requerido").test({
        name: 'valida-meses-graciaK-tabla-cuota-minima',
        exclusive: false,
        params: {},
        message: 'Debe seleccionar una tabla de desarrollo de cuota mínima en base a estos meses de gracia',
        test: (value, context) => value === context.parent.periodosGraciaCapitalTablaCuotaMinima,
    }
    ).test({
        name: 'valida-meses-graciaK-tabla-cuota-maxima',
        exclusive: false,
        params: {},
        message: 'Debe seleccionar una tabla de desarrollo de cuota máxima en base a estos meses de gracia',
        test: (value, context) => {
            if (tipoProductosSinCoutaMaxima.includes(context.parent.tipoProducto)) return true;
            return value === context.parent.periodosGraciaCapitalTablaCuotaMaxima
        }
    }),

    mesesGraciaKI: yup.number().required("Campo requerido").test({
        name: 'valida-meses-graciaKI-tabla-cuota-minima',
        exclusive: false,
        params: {},
        message: 'Debe seleccionar una tabla de desarrollo de cuota mínima en base a estos meses de gracia',
        test: (value, context) => value === context.parent.periodosGraciaCapitalInteresesTablaCuotaMinima,
    }
    ).test({
        name: 'valida-meses-graciaKI-tabla-cuota-maxima',
        exclusive: false,
        params: {},
        message: 'Debe seleccionar una tabla de desarrollo de cuota máxima en base a estos meses de gracia',
        test: (value, context) => {
            if (tipoProductosSinCoutaMaxima.includes(context.parent.tipoProducto)) return true;
            return value === context.parent.periodosGraciaCapitalInteresesTablaCuotaMaxima
        }
    }),

    montoUltimoDividendo: yup.number().required('Campo requerido'),
    tasaEndoso: yup.number().required('Campo requerido'),

    plazoMaximo: yup.number().nullable().oneOf([yup.ref('plazoAnualTablaCuotaMinima')],
        "Debe seleccionar una tabla de desarrollo de cuota mínima en base a este plazo"),

    plazoMinimo: yup.number().nullable().when([], {
        is: true,
        then: (s) => s.max(yup.ref('plazoMaximo'), "plazo mínimo debe ser menor a plazo máximo")
    })
        .when(["plazoMinimo", "tipoProducto"], {
            is: (plazo: number, tipo: string) => {
                return !!(plazo && plazo > 0 && !tipoProductosSinCoutaMaxima.includes(tipo));
            },
            then: (s) => s.oneOf([yup.ref('plazoAnualTablaCuotaMaxima')],
                "Debe seleccionar una tabla de desarrollo de cuota máxima en base a este plazo"),
        }),

    precioVenta: yup.number().required('Campo requerido'),
    montoCredito: yup.number().required('Campo requerido').test({
        name: 'valida-monto-credito',
        exclusive: false,
        params: {},
        message: 'Monto mutuo y monto crédito deben ser iguales',
        test: (value, context) => context.parent.montoMutuo === value
    }),
    montoMutuo: yup.number().required('Campo requerido').test({
        name: 'valida-monto-credito',
        exclusive: false,
        params: {},
        message: 'Monto mutuo y monto crédito deben ser iguales',
        test: (value, context) => context.parent.montoCredito === value
    }),
    tasaEmision: yup.number().required("Campo requerido").test({
        name: 'valida-tasa-minima',
        exclusive: false,
        params: {},
        message: 'Debe seleccionar una tabla de desarrollo de cuota mínima en base a esta tasa',
        test: (value, context) => value === context.parent.tasaTablaCuotaMinima,
    }
    ).test({
        name: 'valida-tasa-maxima',
        exclusive: false,
        params: {},
        message: 'Debe seleccionar una tabla de desarrollo de cuota máxima en base a esta tasa',
        test: (value, context) => {
            if (tipoProductosSinCoutaMaxima.includes(context.parent.tipoProducto)) return true;
            return value === context.parent.tasaTablaCuotaMaxima
        }
    }),

    pagoContado: yup.number().nullable().min(0, "Pago contado debe ser mayor o igual a 0"),
    gastosOperascionales: yup.number().nullable(),
    esGastosIncluidosCredito: yup.boolean().required('Campo requerido'),
    spread: yup.number().nullable(),
    finalidadCredito: yup.string().required('Campo requerido'),

    dfL2: yup.boolean().required('Campo requerido'),
    numeroPropiedades: yup.number().nullable(),

    fechaFirmaPrimeraEscritura: yup.date().nullable(),

    tasaTablaCuotaMaxima: yup.number().nullable(),
    nombreTablaCuotaMaxima: yup.string().nullable(),
    cuotaMaxima: yup.number().nullable(),

    tasaTablaCuotaMinima: yup.number().required('Campo requerido'),
    nombreTablaCuotaMinima: yup.string().required('Campo requerido'),
    cuotaMinima: yup.number().required('Campo requerido'),

    utilidad: yup.string().required('Campo requerido'),
    origen: yup.string().required('Campo requerido'),
    canalVenta: yup.string().required('Campo requerido'),

    //beneficio
    tipoBeneficio: yup.string().nullable(),
    porcentajeDcto: yup.number().nullable().test({
        name: 'valida-porcentaje-dcto',
        exclusive: false,
        params: {},
        message: 'Para ingresar un porcentaje de descuento, debe seleccionar un tipo beneficio',
        test: (value, context) => {
            if (!value)
                return true;

            return context.parent.tipoBeneficio !== null
        }
    }),

    //subsidio
    tieneSubsidio: yup.boolean().required('Campo requerido'),
    tipoSubsidio: yup.string().nullable(),
    ahorro: yup.number().nullable(),
    bonoCaptacion: yup.number().nullable(),
    bonoIntegracion: yup.number().nullable(),
    subsidio: yup.number().nullable(),
    codigoBanco: yup.string().nullable(),
    codProyecto: yup.string().max(20, "Máximo 20 caracteres").nullable(),
    resolucion: yup.string().max(20, "Máximo 20 caracteres").nullable(),
    libreta: yup.string().max(20, "Máximo 20 caracteres").nullable(),
    serie: yup.string().max(15, "Máximo 15 caracteres").nullable(),

    //refinanciamiento
    refinanciamiento: yup.number().nullable().when(['destinoCredito'], {
        is: (destinoCredito: string) => destinoCredito === EnumDestinoCredito.Refinanciamiento,
        then: s => s.required('Campo requerido'),
        otherwise: s => s.notRequired()
    }),
    refinancMontoLey: yup.number().nullable().when(['destinoCredito'], {
        is: (destinoCredito: string) => destinoCredito === EnumDestinoCredito.Refinanciamiento,
        then: s => s.required('Campo requerido'),
        otherwise: s => s.notRequired()
    }),
    refinancGenerales: yup.number().nullable().when(['destinoCredito'], {
        is: (destinoCredito: string) => destinoCredito === EnumDestinoCredito.Refinanciamiento,
        then: s => s.required('Campo requerido'),
        otherwise: s => s.notRequired()
    }),
    refinancProvBanco: yup.number().nullable().when(['destinoCredito'], {
        is: (destinoCredito: string) => (destinoCredito === EnumDestinoCredito.Refinanciamiento || destinoCredito === EnumDestinoCredito.Adquisicion),
        then: s => s.required('Campo requerido'),
        otherwise: s => s.notRequired()
    }),
    refinancDivsFuturosBanco: yup.number().nullable().when(['destinoCredito'], {
        is: (destinoCredito: string) => destinoCredito === EnumDestinoCredito.Refinanciamiento,
        then: s => s.required('Campo requerido'),
        otherwise: s => s.notRequired()
    }),

    //adquisicion
    adquisicion: yup.number().nullable().when(['destinoCredito'], {
        is: (destinoCredito: string) => destinoCredito === EnumDestinoCredito.Adquisicion,
        then: s => s.required('Campo requerido'),
        otherwise: s => s.notRequired()
    }),
    liquidacionPrepagoVendedor: yup.number().nullable().when(['destinoCredito'], {
        is: (destinoCredito: string) => destinoCredito === EnumDestinoCredito.Adquisicion,
        then: s => s.required('Campo requerido'),
        otherwise: s => s.notRequired()
    }),
},
    [
        ["plazoMinimo", "plazoMinimo"]
    ]
);


const OperacionForm: React.FC<FormProps<any>> = ({ idMutuo, isComplementario, esMutuoNormalyConComplementario, idSolicitud, isDesactivado, esVigente }) => {
    const {
        tipoProductos,
        listaDestinos,
        listaOrigenes,
        finalidades,
        tipoSubsidios,
        beneficios,
        utilidades,
        canalVentas,
        prepagos,
        adquisicion,
        refinanciamiento,
        defaultValues,
        listarSelectores, obtenerOperacion } = useOperacion();
    const {
        formState: { errors, isDirty, isValid },
        handleSubmit,
        reset,
        control,
        setValue,
        watch,
        trigger
    } = useForm<operacion>({
        mode: 'onChange',
        defaultValues: defaultValues,
        resolver: yupResolver(validationSchema)
    })
    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });
    const [isEditing, setIsEditing] = useState(false);
    const originalMontoCredito = useRef<number | null | undefined>(undefined);
    const [errorTablaCuotaMinima, setErrorTablaCuotaMinima] = useState<boolean>(false);
    const [errorTablaCuotaMaxima, setErrorTablaCuotaMaxima] = useState<boolean>(false);
    const { addToast } = useToast();
    const data = watch();
    const { hasPermission } = useUserRolesContext();

    useEffect(() => {
        listarSelectores();
        obtenerOperacion(idMutuo);
    }, [idMutuo]);

    useEffect(() => {
        if (!data.dfL2)
            setValue('numeroPropiedades', 0)

    }, [data.dfL2]);

    useEffect(() => {
        if (data.destinoCredito !== EnumDestinoCredito.Adquisicion) {
            setValue('adquisicion', null);
            setValue('liquidacionPrepagoVendedor', null);
        }

        if (data.destinoCredito !== EnumDestinoCredito.Refinanciamiento) {
            setValue('refinanciamiento', null);
            setValue('refinancMontoLey', null);
            setValue('refinancGenerales', null);
            setValue('refinancDivsFuturosBanco', null);
        }

        if (data.destinoCredito !== EnumDestinoCredito.Refinanciamiento && data.destinoCredito !== EnumDestinoCredito.Adquisicion) {
            setValue('refinancProvBanco', null);
        }

        if (data.destinoCredito === EnumDestinoCredito.Adquisicion) {
            trigger(['liquidacionPrepagoVendedor', 'adquisicion', 'refinancProvBanco']);
        }

        if (data.destinoCredito === EnumDestinoCredito.Refinanciamiento) {
            trigger(['refinancDivsFuturosBanco', 'refinancProvBanco', 'refinancGenerales', 'refinancMontoLey', 'refinanciamiento']);
        }

    }, [data.destinoCredito]);

    useEffect(() => {
        reset(defaultValues);
        originalMontoCredito.current = defaultValues.montoCredito;
    }, [defaultValues, reset]);

    const [subsidioData, setSubsidioData] = useState<SubsidioMutuo>();

    const setDatosSubsidio = (subsidioFormData: SubsidioMutuo | undefined) => {

        setSubsidioData(subsidioFormData);

    };

    const handleCalculoPagoContado = async () => {

        const precioVenta = data.precioVenta ? data.precioVenta : 0;
        const montoCredito = data.montoCredito ? data.montoCredito : 0;
        const subsidio = data.tipoSubsidio != null && data?.subsidio ? data?.subsidio : 0;
        const ahorro = data.tipoSubsidio != null && data?.ahorro ? data?.ahorro : 0;
        const bonoCaptacion = data.tipoSubsidio != null && data?.bonoCaptacion ? data?.bonoCaptacion : 0;
        const bonoIntegracion = data.tipoSubsidio != null && data?.bonoIntegracion ? data?.bonoIntegracion : 0;

        let nuevoMontoPagoContado: number = 0;
        if (data.esGastosIncluidosCredito && data.gastosOperacionales) {
            nuevoMontoPagoContado = precioVenta - (subsidio + ahorro + bonoCaptacion + bonoIntegracion + montoCredito - data.gastosOperacionales);
        }
        else {
            nuevoMontoPagoContado = precioVenta - (subsidio + ahorro + bonoCaptacion + bonoIntegracion + montoCredito);
        }

        setValue("pagoContado", nuevoMontoPagoContado);

    }

    useEffect(() => {

        if (data.destinoCredito !== EnumDestinoCredito.Refinanciamiento)
            handleCalculoPagoContado();
        else
            setValue("pagoContado", 0);

    }, [
        data.destinoCredito,
        data.tipoSubsidio,
        data.montoCredito,
        data.esGastosIncluidosCredito,
        data.precioVenta,
        data.gastosOperacionales,
        data.subsidio,
        data.ahorro,
        data.bonoCaptacion,
        data.bonoIntegracion
    ]);

    useEffect(() => {
        trigger(['montoMutuo', 'montoCredito']);
    }, [data.montoMutuo, data.montoCredito, trigger]);

    const handleCanceled = () => {
        if (isDirty) {
            setModalConfirmacion({
                show: true,
                title: "Hay cambios sin guardar",
                message: "¿Desea abandonar la edición?",
                metodo: () => {
                    setIsEditing(false);
                    reset();
                },
            });
        }
        else {
            setIsEditing(false);
            reset();
        }
    }

    const executeSave = useCallback(async (data: operacion) => {
        data.idMutuo = idMutuo;
        const response = await putOperacion(data);
        if (response.status === StatusCode.NoContent) {
            console.log("OPERACION MODIFICADA")
            addToast({
                mostrar: true,
                titulo: 'Confirmación',
                tipo: 'Success',
                mensaje: 'Datos modificados correctamente'
            });
            setIsEditing(false);
            obtenerOperacion(idMutuo);
        }
    }, [idMutuo, obtenerOperacion, addToast]);

    const onSubmit = useCallback(async (data: operacion) => {
        if (errorTablaCuotaMinima || errorTablaCuotaMaxima)
            return;
       
        if (data.montoCredito !== originalMontoCredito.current) {
            setModalConfirmacion({
                show: true,
                title: "Advertencia",
                message: "Al modificar el monto del crédito debe asegurarse de actualizar los seguros ya ingresados y las garantías. ¿Está seguro de continuar?",
                metodo: () => { executeSave(data); },
            });
            return;
        }

        await executeSave(data);
    }, [errorTablaCuotaMinima, errorTablaCuotaMaxima, esMutuoNormalyConComplementario, executeSave]);

    const handleCopy = () => {
        setModalConfirmacion({
            show: true,
            title: "Generar Copia",
            message: '¿Desea copiar la información de la operación desde el mutuo normal?',
            metodo: () => {
                onCopy()
            },
        });
    }

    const onCopy = async () => {
        if (idSolicitud != null) {
            const resp = await copyOperacion(idSolicitud);
            if (resp.status === StatusCode.NoContent) {
                setIsEditing(false);
                addToast({
                    mostrar: true,
                    titulo: 'Confirmación',
                    tipo: 'Success',
                    mensaje: 'Datos copiados correctamente'
                });

                obtenerOperacion(idMutuo);
            }
        }
    }

    const permisoCarteraMutuoEditarInfo = hasPermission(TipoFuncionalidad.Cartera_editar_info);

    const isReadonlyStandardCarteraMutuo = () => {
        let result = !isEditing || isComplementario || esVigente;
        return result ?? true;
    }

    const isReadonlyWithPermission = () => {
        let result = !isEditing || isComplementario || (esVigente && !permisoCarteraMutuoEditarInfo);
        return result ?? true;
    }

    const isReadonlyWithPermissionComplementario = () => {
        let result = !isEditing || (esVigente && !permisoCarteraMutuoEditarInfo);
        return result ?? true;
    }
    return (
        <>
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({
                        ...modalConfirmacion,
                        show: false,
                    })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                title={modalConfirmacion.title}
                message={modalConfirmacion.message}
            />
            <form onSubmit={handleSubmit(onSubmit)}>

                {/* <FirmaEscrituraForm idMutuo={idMutuo} fechaFirmaPrimeraEscritura={data.fechaFirmaPrimeraEscritura}/> */}
                {!isEditing ?
                    <DetalleMutuoPermissionGuard
                        esVigente={esVigente}
                        permissions={[TipoFuncionalidad.Bandeja_editar, TipoFuncionalidad.Cartera_editar_info]}>
                        <Grid2 container spacing={1} size={12} sx={{ textAlign: "right", justifyContent: 'flex-end', mt: 2 }}>
                            {isComplementario ?
                                <MButton
                                    variant="contained"
                                    startIcon={<Edit />}
                                    onClick={() => { handleCopy(); }}>
                                    COPIAR DESDE MUTUO NORMAL
                                </MButton>
                                : <></>}

                            <MButton
                                variant="contained"
                                startIcon={<Edit />}
                                disabled={isDesactivado}
                                onClick={() => { setIsEditing(true); trigger(); }}>
                                Editar
                            </MButton>
                        </Grid2>
                    </DetalleMutuoPermissionGuard>
                    :
                    <Grid2 container spacing={1} size={12} sx={{ textAlign: "right", justifyContent: 'flex-end', mt: 2 }}>
                        <MButton
                            variant='contained'
                            color='primary'
                            startIcon={<SaveAlt />}
                            disabled={!(isDirty && isValid)}
                            onClick={handleSubmit(onSubmit)}>
                            Guardar
                        </MButton>

                        <MButton
                            variant='contained'
                            color={'inherit'}
                            startIcon={<Close />}
                            onClick={handleCanceled}
                        >
                            Cancelar
                        </MButton>
                    </Grid2>
                }
                <Divider sx={{ my: 2 }} />
                <Grid2 sx={{ mt: 2, mb: 3 }}>

                    <Card sx={{ mb: 2, backgroundcolor: 'white' }} raised>
                        <CardContent>
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Características operación
                            </Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputText
                                        label='Tipo mutuo'
                                        name={'tipoMutuo'}
                                        control={control}
                                        readonly
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label='Tipo producto'
                                        name={'tipoProducto'}
                                        control={control}
                                        options={tipoProductos}
                                        readonly={isReadonlyStandardCarteraMutuo()}
                                        error={errors.tipoProducto}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label='Destino crédito'
                                        name={'destinoCredito'}
                                        control={control}
                                        options={listaDestinos}
                                        readonly={isReadonlyWithPermission()}
                                        error={errors.destinoCredito}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputFecha
                                        label='Fecha primera firma'
                                        control={control}
                                        name="fechaFirmaPrimeraEscritura"
                                        fecha={data.fechaFirmaPrimeraEscritura}
                                        esReadOnly={!isEditing || isComplementario || esVigente}
                                        error={errors.fechaFirmaPrimeraEscritura}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }} sx={{ mt: 3 }} >
                                    <InputSwitch
                                        label="Tiene subsidio"
                                        name="tieneSubsidio"
                                        control={control}
                                        disabled={isReadonlyWithPermission()}
                                        error={errors.tieneSubsidio}
                                    />
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>
                    <Card sx={{ mb: 2, backgroundcolor: 'white' }} raised>
                        <CardContent>
                            <Typography className='card-title' variant="h6" gutterBottom>
                                Detalle operación
                            </Typography>
                            <Grid2 container spacing={2} mt={2}>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Precio venta (MO)"
                                        name="precioVenta"
                                        control={control}
                                        prefix="UF"
                                        decimales={4}
                                        minValue={0}
                                        maxValue={10000000000}
                                        readonly={isReadonlyWithPermission()}
                                        error={errors.precioVenta}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Monto crédito (MO)"
                                        name="montoCredito"
                                        control={control}
                                        minValue={0}
                                        maxValue={10000000000}
                                        prefix="UF"
                                        decimales={4}
                                        readonly={!isEditing || esVigente}
                                        error={errors.montoCredito}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Monto mutuo (MO)"
                                        name="montoMutuo"
                                        control={control}
                                        prefix="UF"
                                        decimales={4}
                                        minValue={0}
                                        maxValue={10000000000}
                                        readonly={!isEditing || esVigente}
                                        error={errors.montoMutuo}
                                    />
                                </Grid2>
                                {data.destinoCredito !== EnumDestinoCredito.Refinanciamiento && (
                                    <Grid2 size={{ xs: 12, lg: 3 }} >
                                        <InputNumber
                                            label='Pago contado'
                                            name="pagoContado"
                                            control={control}
                                            prefix="UF"
                                            readonly
                                            decimales={4}
                                            minValue={0}
                                            maxValue={10000000000}
                                            error={errors.pagoContado}
                                        />
                                    </Grid2>
                                )}
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Tasa emisión"
                                        name="tasaEmision"
                                        control={control}
                                        maxValue={100}
                                        suffix="%"
                                        error={errors.tasaEmision}
                                        decimales={4}
                                        readonly={!isEditing || esVigente}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Meses gracia K"
                                        name="mesesGraciasK"
                                        control={control}
                                        decimales={0}
                                        minValue={0}
                                        suffix="meses"
                                        error={errors.mesesGraciasK}
                                        readonly={!isEditing || isComplementario || esVigente}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Meses gracia K+I"
                                        name="mesesGraciaKI"
                                        control={control}
                                        decimales={0}
                                        suffix="meses"
                                        error={errors.mesesGraciaKI}
                                        readonly={!isEditing || isComplementario || esVigente}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Monto último dividendo"
                                        name="montoUltimoDividendo"
                                        control={control}
                                        prefix="UF"
                                        decimales={4}
                                        readonly={!isEditing || isComplementario || esVigente}
                                        error={errors.montoUltimoDividendo}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label="Prepago"
                                        name="prepago"
                                        control={control}
                                        options={prepagos}
                                        readonly={isReadonlyWithPermission()}
                                        error={errors.prepago}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label='Comisión prepago'
                                        name="comisionPrepago"
                                        control={control}
                                        suffix='%'
                                        maxValue={100}
                                        minValue={0}
                                        decimales={4}
                                        //prefix='%'
                                        error={errors.comisionPrepago}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputSelect
                                        label="Tipo beneficio"
                                        name="tipoBeneficio"
                                        control={control}
                                        options={beneficios}
                                        readonly={isReadonlyWithPermission()}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Porcentaje descuento"
                                        name="porcentajeDcto"
                                        control={control}
                                        maxValue={100}
                                        suffix="%"
                                        readonly={isReadonlyWithPermission()}
                                        error={errors.porcentajeDcto}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Tasa endoso"
                                        name="tasaEndoso"
                                        control={control}
                                        suffix="%"
                                        maxValue={100}
                                        minValue={0}
                                        decimales={4}
                                        readonly={isReadonlyWithPermissionComplementario()}
                                        error={errors.tasaEndoso}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label='Spread'
                                        name="spread"
                                        control={control}
                                        maxValue={100}
                                        suffix="%"
                                        decimales={4}
                                        readonly={isReadonlyWithPermissionComplementario()}
                                        error={errors.spread}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label='Gastos operacionales'
                                        name="gastosOperacionales"
                                        control={control}
                                        prefix="UF"
                                        decimales={4}
                                        maxValue={10000000000}
                                        readonly={isReadonlyWithPermission()}
                                        error={errors.gastosOperacionales}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, lg: 3 }} sx={{ mt: 3 }} >
                                    <InputSwitch
                                        label="Gastos incluidos en crédito"
                                        name="esGastosIncluidosCredito"
                                        control={control}
                                        disabled={isReadonlyWithPermission()}
                                        error={errors.esGastosIncluidosCredito}
                                    />
                                </Grid2>
                                {data.tipoDeudor === enumTipoPersona.Natural && (
                                    <>
                                        <Grid2 size={{ xs: 12, lg: 1 }} sx={{ mt: 3 }} >
                                            <InputSwitch
                                                label="DFL2"
                                                name="dfL2"
                                                control={control}
                                                disabled={isReadonlyWithPermission()}
                                                error={errors.dfL2}
                                            />
                                        </Grid2>

                                        <Grid2 size={{ xs: 12, lg: 2 }} >
                                            <InputNumber
                                                label='N° propiedades'
                                                name="numeroPropiedades"
                                                control={control}
                                                readonly={!(data.dfL2 && isEditing) || isComplementario || (esVigente && !hasPermission(TipoFuncionalidad.Cartera_editar_contacto))}
                                                decimales={0}
                                                error={errors.numeroPropiedades}
                                            />
                                        </Grid2>
                                    </>
                                )}

                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                    <InputNumber
                                        label="Día pago"
                                        name="diaPago"
                                        control={control}
                                        decimales={0}
                                        minValue={0}
                                        maxValue={31}
                                        readonly={isReadonlyWithPermission()}
                                        error={errors.diaPago}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <TablasDesarrolloForm
                                        idMutuo={idMutuo}
                                        control={control}
                                        watch={watch}
                                        errors={errors}
                                        trigger={trigger}
                                        setValue={setValue}
                                        isEditing={isEditing}
                                        isComplementario={isComplementario}
                                        esVigente={esVigente}
                                        errorTablaCuotaMinima={errorTablaCuotaMinima}
                                        errorTablaCuotaMaxima={errorTablaCuotaMaxima}
                                        setErrorTablaCuotaMinima={setErrorTablaCuotaMinima}
                                        setErrorTablaCuotaMaxima={setErrorTablaCuotaMaxima}
                                    />
                                </Grid2>
                            </Grid2>
                        </CardContent>
                    </Card>
                </Grid2>
                {
                    data.tieneSubsidio &&
                    <SubsidioForm
                        control={control}
                        mutuoId={idMutuo}
                        setDatosSubsidio={setDatosSubsidio}
                        tipoSubsidios={tipoSubsidios}
                        beneficios={beneficios}
                        isEditing={isEditing}
                        isComplementario={isComplementario}
                        isReadonlyWithPermission={isReadonlyWithPermission}
                        isReadonlyStandardCarteraMutuo={isReadonlyStandardCarteraMutuo}
                        error={errors}
                    />
                }


                {
                    data.destinoCredito === EnumDestinoCredito.Refinanciamiento && (
                        <RefinanciamientoForm
                            control={control}
                            watch={watch}
                            setValue={setValue}
                            refinanciamiento={refinanciamiento}
                            isEditing={isEditing}
                            errors={errors}
                            isComplementario={isComplementario}
                            isReadonlyWithPermission={isReadonlyWithPermission}
                        />
                    )
                }

                {
                    data.destinoCredito === EnumDestinoCredito.Adquisicion &&
                    <AdquisicionForm
                        control={control}
                        adquisicion={adquisicion}
                        isEditing={isEditing}
                        errors={errors}
                        isComplementario={isComplementario}
                        esVigente={esVigente}
                        isReadonlyWithPermission={isReadonlyWithPermission}
                    />
                }
                <OtroCreditoForm
                    control={control}
                    finalidades={finalidades}
                    utilidades={utilidades}
                    listaOrigenes={listaOrigenes}
                    canalVentas={canalVentas}
                    isEditing={isEditing}
                    errors={errors}
                    isComplementario={isComplementario}
                    isReadonlyWithPermission={isReadonlyWithPermission}
                    esVigente={esVigente}
                    permisoCarteraMutuoEditarInfo={permisoCarteraMutuoEditarInfo}
                />
            </form >
        </>
    );
};

export default OperacionForm;